/**
 * useSimCheckChat Hook
 *
 * Manages communication with the AI chat Firebase Function.
 * Handles SSE streaming, authentication, conversation state, and actions.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { auth } from '../config/firebase';
import { FUNCTIONS_URL } from '@/config/env';

// Per-user localStorage key for the last active conversation. Including the
// uid avoids one user's conversation being resumed for another in a shared
// browser. Falls back to the server's "most recent conversation for this
// user" via GET /aiConversations?limit=1 when localStorage is empty/cleared.
function lastConversationStorageKey(uid: string | null | undefined): string | null {
  return uid ? `simcheck.ai.lastConversationId:${uid}` : null;
}

// Cloud Functions base URL for this environment
const FUNCTIONS_BASE = FUNCTIONS_URL;

export interface ChatAction {
  id: string;
  label: string;
  type: 'approve' | 'reject' | 'choice';
  payload?: Record<string, unknown>;
}

export type ThinkingEvent =
  | { kind: 'tool_call'; id: string; seq: number; toolName: string; input: unknown; status: 'pending' | 'done'; elapsed?: number; result?: unknown }
  | { kind: 'skill_read'; id: string; seq: number; file: string }
  | { kind: 'system'; id: string; seq: number; subtype: string; details?: Record<string, unknown> };

/**
 * A tool call the assistant wants to make that needs the user's consent
 * (run_test and friends). The server parks the call, writes a
 * chat_approvals doc and streams an `actions` event; the user's Approve /
 * Reject must reach POST /aiChatApprove, which flips the doc and unblocks
 * the server. A chat message saying "yes" does NOT count — the server only
 * watches the doc.
 */
export interface PendingApproval {
  approvalId: string;
  tool: string;
  input: Record<string, unknown>;
  /** Human-readable lines describing what will run. */
  summary: string[];
  /** 'expired': the turn ended without a decision (the server times out and denies). */
  decision?: 'approve' | 'reject' | 'expired';
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  actions?: ChatAction[];
  thinkingEvents?: ThinkingEvent[];
  pendingApproval?: PendingApproval;
}

const RAT_LABELS: Record<string, string> = { gsm: '2G', wcdma: '3G', lte: '4G / LTE', lte_m: 'LTE-M (Cat-M1)', nb_iot: 'NB-IoT', nr5g: '5G SA', nr5g_nsa: '5G NSA' };

/** Turn a gated tool's raw input into the lines a person needs before saying yes. */
export function describeApprovalInput(tool: string, input: Record<string, unknown>): string[] {
  const s = (k: string) => (typeof input[k] === 'string' && (input[k] as string).trim() ? (input[k] as string).trim() : null);
  const lines: string[] = [];
  const short = tool.replace(/^mcp__simcheck__/, '');
  if (short === 'run_test' || short === 'run_test_on_cluster') {
    lines.push(`Flow: ${s('flowName') ?? s('flowId') ?? 'unknown flow'}`);
    const where = s('place') ?? s('locationId') ?? s('countryCode') ?? (s('clusterId') ? `cluster ${s('clusterId')}` : null);
    if (where) lines.push(`Where: ${where}`);
    if (s('simCarrier')) lines.push(`SIM: ${s('simCarrier')}`);
    else if (s('esimProfileId')) lines.push(`eSIM profile: ${s('esimProfileId')}`);
    else if (s('esimSelection') === 'auto') lines.push('eSIM: chosen automatically by evidence');
    else lines.push('SIM: whichever is in the allocated modem (no eSIM)');
    if (s('forcedRat') && s('forcedRat') !== 'auto') lines.push(`Technology: ${RAT_LABELS[s('forcedRat')!] ?? s('forcedRat')}`);
    if (s('forcedNetworkMccMnc')) lines.push(`Network: PLMN ${s('forcedNetworkMccMnc')}`);
    if (input.queueIfBusy === true) lines.push('Will wait in the queue if the modem is busy');
    if (input.allowNearest === true) lines.push('May run at the nearest site in another country');
    if (input.confirmConsumeSingleUse === true) lines.push('⚠ Spends the SIM\'s last activation: it will be tied to that modem for good');
    if (input.allowRelocate === true) lines.push('⚠ Deletes the eSIM from the modem it is on and downloads it again here');
    return lines;
  }
  for (const [k, v] of Object.entries(input)) if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') lines.push(`${k}: ${String(v)}`);
  return lines.slice(0, 8);
}

interface UseSimCheckChatReturn {
  messages: ChatMessage[];
  isStreaming: boolean;
  /** Loading the last conversation from the server; the panel shows a loader, not the empty state. */
  isHydrating: boolean;
  conversationId: string | null;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  decideApproval: (approvalId: string, decision: 'approve' | 'reject') => Promise<void>;
  clearMessages: () => void;
}

/**
 * `enabled` defers loading the last conversation until the chat is first
 * opened. The caller keeps the hook mounted for the page's lifetime, so
 * minimizing or closing the panel never throws the conversation away.
 */
export function useSimCheckChat(enabled = true): UseSimCheckChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isHydrating, setIsHydrating] = useState(false);
  // Set once the user sends or clears: a history load that finishes later
  // must not replace what they are doing (it used to wipe the new question
  // and leave its answer streaming into a message no longer on screen).
  const userActedRef = useRef(false);
  const hydratedRef = useRef(false);
  const [conversationId, setConversationIdState] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const thinkingSeqRef = useRef(0);

  // Persist conversationId to per-user localStorage on every change so it
  // survives ChatWidget unmount (closing the panel) AND tab reload. The
  // backend already keeps the conversation in Firestore — we just need to
  // remember which one we were in.
  const setConversationId = useCallback((id: string | null) => {
    setConversationIdState(id);
    const uid = auth.currentUser?.uid;
    const key = lastConversationStorageKey(uid);
    if (!key) return;
    if (id) localStorage.setItem(key, id);
    else localStorage.removeItem(key);
  }, []);

  const getAuthHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    const token = await user.getIdToken();
    const orgId = localStorage.getItem('viewAsOrgId') || localStorage.getItem('activeOrganizationId') || '';
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(orgId ? { 'X-Organization-Id': orgId } : {}),
    };
  }, []);

  // Rehydrate on mount: try localStorage first, then fall back to
  // GET /aiConversations?limit=1 (the user's most-recent conversation).
  // Either path ends with us calling GET /aiConversations/:id/messages to
  // re-render the prior chat history. If the user has no past conversations,
  // we just leave conversationId null and start fresh on next sendMessage.
  useEffect(() => {
    if (!enabled || hydratedRef.current) return;
    hydratedRef.current = true;
    let cancelled = false;
    let finished = false;
    (async () => {
      setIsHydrating(true);
      try {
        await auth.authStateReady();
        const uid = auth.currentUser?.uid;
        if (!uid) return;

        const lsKey = lastConversationStorageKey(uid);
        let candidateId = lsKey ? localStorage.getItem(lsKey) : null;
        const headers = await getAuthHeaders();

        if (!candidateId) {
          // Server-side fallback — survives localStorage clear / different
          // browser / different device. Filter to status=active so a
          // conversation the user explicitly closed (trash button) doesn't
          // reappear.
          const r = await fetch(`${FUNCTIONS_BASE}/aiConversations?limit=1&status=active`, { headers });
          if (r.ok) {
            const j = await r.json() as { conversations?: { id: string }[] };
            candidateId = j.conversations?.[0]?.id ?? null;
          }
        }

        if (!candidateId || cancelled || userActedRef.current) return;

        const r = await fetch(
          `${FUNCTIONS_BASE}/aiConversations/${encodeURIComponent(candidateId)}/messages`,
          { headers },
        );
        if (!r.ok) return;
        const j = await r.json() as {
          conversationId: string;
          messages: { role: 'user' | 'assistant'; content: string; timestamp?: string }[];
        };
        if (cancelled || userActedRef.current) return;
        // Empty messages = either a brand-new conversation (system/init fired
        // but no append yet — see backend comment) or a conversation with only
        // tool/system events. Skip rehydrate so we don't display an empty
        // pre-loaded thread; the SDK's `resume:` will pick the same session_id
        // back up on the next sendMessage.
        if (!j.messages?.length) {
          setConversationId(j.conversationId);
          return;
        }
        const hydrated: ChatMessage[] = j.messages.map((m, i) => ({
          id: `restored-${j.conversationId}-${i}`,
          role: m.role,
          content: m.content,
          timestamp: m.timestamp || new Date().toISOString(),
        }));
        setMessages(hydrated);
        setConversationId(j.conversationId);
      } catch {
        // Silent — first message will start a fresh conversation if rehydrate fails.
      } finally {
        finished = true;
        if (!cancelled) setIsHydrating(false);
      }
    })();
    return () => {
      cancelled = true;
      // StrictMode re-runs the effect after an immediate cleanup: let it load again.
      if (!finished) hydratedRef.current = false;
    };
  }, [enabled, getAuthHeaders, setConversationId]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isStreaming) return;
    userActedRef.current = true;
    setIsHydrating(false);
    setError(null);

    // Add user message immediately
    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);

    // Add placeholder for assistant response
    const assistantMsgId = `assistant-${Date.now()}`;
    const assistantMsg: ChatMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, assistantMsg]);
    setIsStreaming(true);

    try {
      const headers = await getAuthHeaders();
      abortControllerRef.current = new AbortController();

      const response = await fetch(`${FUNCTIONS_BASE}/aiChatStream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: content, conversationId }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (!data) continue;

          try {
            const event = JSON.parse(data);
            switch (event.type) {
              case 'conversation':
                setConversationId(event.conversationId);
                break;
              case 'text':
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? { ...msg, content: msg.content + event.text }
                    : msg
                ));
                break;
              case 'tool_call': {
                const seq = ++thinkingSeqRef.current;
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        thinkingEvents: [
                          ...(msg.thinkingEvents ?? []),
                          { kind: 'tool_call', id: event.id || `tc-${seq}`, seq, toolName: event.name, input: event.input, status: 'pending' },
                        ],
                      }
                    : msg
                ));
                break;
              }
              case 'tool_progress': {
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        thinkingEvents: (msg.thinkingEvents ?? []).map(e =>
                          e.kind === 'tool_call' && e.id === event.id
                            ? { ...e, elapsed: event.elapsed }
                            : e
                        ),
                      }
                    : msg
                ));
                break;
              }
              case 'tool_result': {
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        thinkingEvents: (msg.thinkingEvents ?? []).map(e =>
                          e.kind === 'tool_call' && e.id === event.id
                            ? { ...e, status: 'done', result: event.content }
                            : e
                        ),
                      }
                    : msg
                ));
                break;
              }
              case 'skill_read': {
                const seq = ++thinkingSeqRef.current;
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        thinkingEvents: [
                          ...(msg.thinkingEvents ?? []),
                          { kind: 'skill_read', id: `sr-${seq}`, seq, file: event.file },
                        ],
                      }
                    : msg
                ));
                break;
              }
              case 'system': {
                // Surface non-noisy system events (e.g. compacted) so users can see context shifts.
                if (event.subtype === 'compacted') {
                  const seq = ++thinkingSeqRef.current;
                  setMessages(prev => prev.map(msg =>
                    msg.id === assistantMsgId
                      ? {
                          ...msg,
                          thinkingEvents: [
                            ...(msg.thinkingEvents ?? []),
                            { kind: 'system', id: `sys-${seq}`, seq, subtype: event.subtype, details: { preTokens: event.preTokens, postTokens: event.postTokens } },
                          ],
                        }
                      : msg
                  ));
                }
                break;
              }
              case 'actions': {
                // The server is now blocked on our decision (it times out
                // after a couple of minutes). Show what will run, plus buttons.
                const input = (event.input && typeof event.input === 'object' ? event.input : {}) as Record<string, unknown>;
                const pending: PendingApproval = { approvalId: event.approvalId, tool: event.tool, input, summary: describeApprovalInput(event.tool, input) };
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        pendingApproval: pending,
                        actions: [
                          { id: `${event.approvalId}:approve`, label: 'Approve', type: 'approve', payload: { approvalId: event.approvalId } },
                          { id: `${event.approvalId}:reject`, label: 'Reject', type: 'reject', payload: { approvalId: event.approvalId } },
                        ],
                      }
                    : msg
                ));
                break;
              }
              case 'done':
                setMessages(prev => prev.map(msg =>
                  msg.id === assistantMsgId
                    ? {
                        ...msg,
                        content: event.fullText || msg.content,
                        actions: event.actions || undefined,
                      }
                    : msg
                ));
                break;
              case 'error':
                setError(event.error);
                break;
            }
          } catch {
            // Ignore malformed JSON
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to send message');
      // Remove empty assistant message on error
      setMessages(prev => prev.filter(msg => msg.id !== assistantMsgId || msg.content));
    } finally {
      // A card still open when the turn ends can no longer be answered: the
      // server denied it on timeout. Say so instead of leaving it "waiting".
      setMessages(prev => prev.map(msg =>
        msg.id === assistantMsgId && msg.pendingApproval && !msg.pendingApproval.decision
          ? { ...msg, actions: undefined, pendingApproval: { ...msg.pendingApproval, decision: 'expired' } }
          : msg
      ));
      setIsStreaming(false);
      abortControllerRef.current = null;
    }
  }, [isStreaming, conversationId, getAuthHeaders]);

  // Explicit "start a new conversation" — user clicked the trash icon
  // in the chat header. Clears local state + the localStorage entry, AND
  // calls POST /aiConversations/:id/close so the rehydrate's server
  // fallback (`?status=active`) doesn't surface this same conversation
  // again on the next mount. Fire-and-forget on the close call: even if
  // it fails, the user gets a fresh local state and the worst case is
  // the closed conversation reappearing on a different device.
  const clearMessages = useCallback(() => {
    userActedRef.current = true;
    setIsHydrating(false);
    const idToClose = conversationId;
    setMessages([]);
    setConversationId(null);
    setError(null);
    if (idToClose) {
      (async () => {
        try {
          const headers = await getAuthHeaders();
          await fetch(
            `${FUNCTIONS_BASE}/aiConversations/${encodeURIComponent(idToClose)}/close`,
            { method: 'POST', headers },
          );
        } catch {
          // Silent — local state already cleared.
        }
      })();
    }
  }, [conversationId, getAuthHeaders, setConversationId]);

  /**
   * Approve or reject a parked tool call. Goes to POST /aiChatApprove, which
   * flips the chat_approvals doc the server is watching. On success the
   * buttons are replaced by the decision; the stream then continues.
   */
  const decideApproval = useCallback(async (approvalId: string, decision: 'approve' | 'reject') => {
    try {
      const headers = await getAuthHeaders();
      const r = await fetch(`${FUNCTIONS_BASE}/aiChatApprove`, { method: 'POST', headers, body: JSON.stringify({ approvalId, decision }) });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || `HTTP ${r.status}`);
      }
      setMessages(prev => prev.map(msg =>
        msg.pendingApproval?.approvalId === approvalId
          ? { ...msg, actions: undefined, pendingApproval: { ...msg.pendingApproval, decision } }
          : msg
      ));
    } catch (err: any) {
      setError(err.message || 'Could not record your decision');
    }
  }, [getAuthHeaders]);

  return { messages, isStreaming, isHydrating, conversationId, error, sendMessage, clearMessages, decideApproval };
}
